import ExcelJS from 'exceljs';

type RagStyle = {
  fillColor: string;
  textColor: string;
};

type DownloadExcelOptions = {
  ragColoringColumns?: number[];
  sheetName?: string;
};

const toArgb = (hex: string, alpha: string = 'FF'): string => {
  return `${alpha}${hex.replace('#', '').toUpperCase()}`;
};

const parsePercentage = (value: any): number | null => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const cleanedValue = value.replace('%', '').replace(',', '').replace(' ', '').trim();
  const parsed = parseFloat(cleanedValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const getRagStyle = (value: any): RagStyle | null => {
  const percentage = parsePercentage(value);
  if (percentage === null) return null;

  if (percentage < 74) {
    return {
      fillColor: toArgb('#EF4444', '33'),
      textColor: toArgb('#FFFFFF'),
    };
  }
  if (percentage >= 75 && percentage < 90) {
    return {
      fillColor: toArgb('#EAB000', '33'),
      textColor: toArgb('#FFFFFF'),
    };
  }
  if (percentage >= 90) {
    return {
      fillColor: toArgb('#22C55E', '33'),
      textColor: toArgb('#FFFFFF'),
    };
  }
  return null;
};

const downloadExcel = async (
  data: any[][],
  filename: string = 'data',
  options: DownloadExcelOptions = {}
): Promise<void> => {
  if (!data || data.length === 0) {
    console.error('No data provided.');
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(options.sheetName || 'Sheet1');

  const headers = data[0] || [];
  const complianceIndices = headers
    .map((h: any, i: number) => (typeof h === 'string' && h.toLowerCase().includes('compliance %') ? i : -1))
    .filter((i: number) => i !== -1);

  data.forEach((row, rIdx) => {
    const normalizedRow = Array.isArray(row)
      ? row.map((cell, cIdx) => {
          if (rIdx > 0 && complianceIndices.includes(cIdx) && cell !== null && cell !== undefined && cell !== '' && !isNaN(Number(cell))) {
            return Math.floor(Number(cell));
          }
          return cell ?? '';
        })
      : [row ?? ''];
    worksheet.addRow(normalizedRow);
  });

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true, size: 13 };
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: toArgb('#F2F2F2') },
    };
  });

  const columnWidths: number[] = [];
  data.forEach((row) => {
    const normalizedRow = Array.isArray(row) ? row : [row ?? ''];
    normalizedRow.forEach((cell, index) => {
      const cellText = cell === null || cell === undefined ? '' : String(cell);
      const width = Math.min(Math.max(cellText.length + 2, 10), 50);
      columnWidths[index] = Math.max(columnWidths[index] || 10, width);
    });
  });
  worksheet.columns = columnWidths.map((width) => ({ width }));

  const ragColumns = options.ragColoringColumns || [];
  if (ragColumns.length > 0 && data.length > 1) {
    for (let rowIndex = 1; rowIndex < data.length; rowIndex += 1) {
      const row = data[rowIndex];
      ragColumns.forEach((columnIndex) => {
        if (!Array.isArray(row)) return;
        const value = row[columnIndex];
        const style = getRagStyle(value);
        if (!style) return;

        const cell = worksheet.getCell(rowIndex + 1, columnIndex + 1);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: style.fillColor },
        };
        cell.font = {
          color: { argb: style.textColor },
        };
      });
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default downloadExcel;
